import React, { useEffect, useState, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useBookingEditText } from "./text";
import { registerLocale } from "react-datepicker";
import moment from "moment";
import ja from "date-fns/locale/ja";
import "react-datepicker/dist/react-datepicker.css";
import jwtDecode from "jwt-decode";

import Layout from "../../layout/Layout";
import TitleBar from "../../layout/TitleBar";
import { createTypographyHelper } from "../../../utils/typography";
import BasicInformationSection from "./components/BasicInformationSection";
import VehicleSelectionSection from "./components/VehicleSelectionSection";
import PassengerInformationSection from "./components/PassengerInformationSection";
import AdditionalInformationServicesSection from "./components/AdditionalInformationServicesSection";
import BookingEditReview from "./components/BookingEditReview";
import CancellationPolicyModal from "./components/BookingEditReview/components/CancellationPolicyModal";
import DiscardEditConfirmationModal from "./components/BookingEditReview/components/DiscardEditConfirmationModal";
import ErrorModal from "./components/ErrorModal";

import BookingHeader from "./components/BookingHeader";
import ActionButtons from "./components/ActionButtons";
import SkeletonLoading from "./components/SkeletonLoading";
import SettingsSection from "./components/SettingsSection";

import {
  LocationPinIcon,
  DEPARTURE_FLIGHT_ICON,
  PASSENGER_TITLE_OPTIONS,
  PASSENGER_TITLE_WITH_EMPTY_OPTIONS,
  TERMINAL_OPTIONS,
  TRAVEL_AREA_OPTIONS,
  LONG_DISTANCE_FEE_TYPE,
} from "./constants";

import {
  enrichPlaceData,
  formatTravelArea,
  formatPassengerTitle,
  formatPickupSignHonorific,
  checkIsPickupHaneda,
  checkIsDropoffHaneda,
  checkLocationUseMeetService,
  checkLocationUseTerminalSelection,
  getDuplicateAdjacentDropoffIds,
} from "./utils";

import {
  useVehicles,
  useMeetAssist,
  useDuration,
  useTransferFlags,
  useChildSeats,
  useBookingDetail,
  useBookingSubmission,
} from "./hooks";

registerLocale("ja", ja);

const BookingEditSkeleton = () => {
  return <SkeletonLoading />;
};

const BookingEditPage = () => {
  const history = useHistory();
  const { t, language } = useBookingEditText();
  const getTypographyClass = createTypographyHelper(language);
  const {
    id: routeId,
    requestId: routeRequestId,
    bookingId: routeBookingId,
  } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isModificationRequestPath = location.pathname.includes("/admin/modification-requests/");
  const requestId =
    routeRequestId ||
    (isModificationRequestPath ? routeId : "") ||
    queryParams.get("requestId") ||
    "";
  const id =
    routeBookingId ||
    queryParams.get("bookingId") ||
    (!isModificationRequestPath ? (queryParams.get("id") || routeId) : queryParams.get("id")) ||
    "";
  const lang = language.toUpperCase();

  if (lang === "EN" || lang === "en") {
    moment.locale("en");
  } else {
    moment.locale("ja");
  }

  // ─── Core form state ───────────────────────────────────────────

  const [transferType, setTransferType] = useState("charter");
  const [bookingFor, setBookingFor] = useState("myself");
  const [passengers, setPassengers] = useState({ adult: 0, child: 0, toddler: 0, baby: 0 });
  const [luggage, setLuggage] = useState({ cabin: 0, checkin: 0 });

  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [pickupLocationData, setPickupLocationData] = useState(null);
  const [dropoffLocationData, setDropoffLocationData] = useState(null);
  const [airportDropoffLocations, setAirportDropoffLocations] = useState([
    { id: 1, address: "", data: null },
  ]);
  const [travelArea, setTravelArea] = useState("neighboringPerfecture");

  const [travelDate, setTravelDate] = useState(new Date("2025-11-29"));
  const [formatedTravelDate, setFormatedTravelDate] = useState("2025-11-29");
  const [pickupTime, setPickupTime] = useState("");

  const [passengerTitle, setPassengerTitle] = useState("Mr.");
  const [passengerName, setPassengerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countriesData, setCountriesData] = useState([]);
  const [prefixPhoneNumber, setPrefixPhoneNumber] = useState("+81");
  const [countryCode, setCountryCode] = useState("JP");

  const [isPassengerNameInvalid, setIsPassengerNameInvalid] = useState(false);
  const [isPassengerPhoneInvalid, setIsPassengerPhoneInvalid] = useState(false);

  const [pickupFlightNo, setPickupFlightNo] = useState("");
  const [dropoffFlightNo, setDropoffFlightNo] = useState("");
  const [pickupTerminal, setPickupTerminal] = useState("");
  const [dropoffTerminal, setDropoffTerminal] = useState("");
  const [pickupSignHonorific, setPickupSignHonorific] = useState("Mr.");
  const [pickupSign, setPickupSign] = useState("");
  const [message, setMessage] = useState("");

  const [isPickupFlightInvalid, setIsPickupFlightInvalid] = useState(false);
  const [isPickupTerminalInvalid, setIsPickupTerminalInvalid] = useState(false);
  const [isPickupSignInvalid, setIsPickupSignInvalid] = useState(false);
  const [isDropoffFlightInvalid, setIsDropoffFlightInvalid] = useState(false);
  const [isDropoffTerminalInvalid, setIsDropoffTerminalInvalid] = useState(false);
  const [basePriceType, setBasePriceType] = useState("tlfPrice");
  const [garageType, setGarageType] = useState("default");
  const [discountType, setDiscountType] = useState("notApplicable");
  const [discountValue, setDiscountValue] = useState(0);
  const [longDistanceFeeType, setLongDistanceFeeType] = useState(LONG_DISTANCE_FEE_TYPE.AUTOMATIC);
  const [longDistanceFeeValue, setLongDistanceFeeValue] = useState(0);
  const [departureGarageId, setDepartureGarageId] = useState("");
  const [returnGarageId, setReturnGarageId] = useState("");
  const [garageOptions, setGarageOptions] = useState([]);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDuplicateLocationModal, setShowDuplicateLocationModal] = useState(false);

  useEffect(() => {
    setIsPassengerNameInvalid(!passengerName || passengerName.trim() === "");
  }, [passengerName]);

  useEffect(() => {
    setIsPassengerPhoneInvalid(!phoneNumber || phoneNumber.trim() === "");
  }, [phoneNumber]);

  const [customer, setCustomer] = useState({});
  const [bookingKey, setBookingKey] = useState("");
  const [bookingStatus, setBookingStatus] = useState("CONFIRMED");
  const [paymentStatus, setPaymentStatus] = useState("CHARGE_PENDING");

  const basicSectionRef = useRef(null);
  const vehicleSectionRef = useRef(null);
  const passengerSectionRef = useRef(null);
  const additionalInfoSectionRef = useRef(null);

  // Get user role from token
  const token = localStorage.getItem("token");
  let userRole = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      userRole = decoded?.user?.id?.role;
    } catch (error) {
      console.error("Error decoding token:", error);
    }
  }

  // ─── Custom hooks ──────────────────────────────────────────────

  const {
    duration,
    setDuration,
    durationOptions,
  } = useDuration({
    transferType,
    pickupLocationData,
    dropoffLocationData,
    lang,
  });

  const {
    vehicles,
    setVehicles,
    availableVehicleTypes,
    isVehicleLoading,
    vehicleOptions,
    handleChangeVehicle,
    handleAddVehicle,
    handleRemoveVehicle,
    formatVehicle,
    renderVehicleCard,
    isInvalidVehicle,
    vehicleValidation
  } = useVehicles({
    transferType,
    pickupLocationData,
    dropoffLocationData,
    airportDropoffLocations,
    formatedTravelDate,
    travelDate,
    pickupTime,
    passengers,
    luggage,
    duration,
    travelArea,
    transferFlags: null, // will be set after useTransferFlags
    lang,
    role: userRole
  });

  const {
    childSeatAllocations,
    setChildSeatAllocations,
    incrementChildSeat,
    decrementChildSeat,
    syncAllocationsToVehicleCount,
  } = useChildSeats({ passengers });

  // Sync child seat allocation slots when vehicles are added/removed
  useEffect(() => {
    syncAllocationsToVehicleCount(vehicles.length);
  }, [vehicles.length, syncAllocationsToVehicleCount]);

  const {
    meetAssistS,
    setMeetAssistS,
    meetAssistPrice,
    onChangeMeetAssist,
  } = useMeetAssist({ formatedTravelDate, pickupTime, passengers });

  const lastDestinationData =
    (airportDropoffLocations?.length
      ? airportDropoffLocations[airportDropoffLocations.length - 1]?.data
      : null) || dropoffLocationData;

  const {
    transferFlags,
    setTransferFlags,
  } = useTransferFlags({
    transferType,
    pickupLocationData,
    lastDestinationData,
    pickupTerminal,
    dropoffTerminal,
    setPickupFlightNo,
    setDropoffFlightNo,
    setPickupTerminal,
    setDropoffTerminal,
    meetAssistPrice,
    setMeetAssistS,
    setPickupSignHonorific,
    setPickupSign
  });

  const {
    isSimulatingReview,
    isSubmittingModification,
    reviewSimulationData,
    setReviewSimulationData,
    handleReviewChanges,
    handleSubmitModificationRequest,
    showErrorModal,
    errorModalText,
    handleCloseErrorModal,
  } = useBookingSubmission({
    id,
    requestId,
    formatedTravelDate,
    travelDate,
    pickupTime,
    pickupLocationData,
    dropoffLocationData,
    airportDropoffLocations,
    transferType,
    passengers,
    luggage,
    duration,
    travelArea,
    transferFlags,
    meetAssistS,
    vehicles,
    availableVehicleTypes,
    childSeatAllocations,
    passengerName,
    passengerTitle,
    prefixPhoneNumber,
    countryCode,
    countriesData,
    phoneNumber,
    message,
    pickupFlightNo,
    dropoffFlightNo,
    pickupTerminal,
    dropoffTerminal,
    pickupSignHonorific,
    pickupSign,
    basePriceType,
    garageType,
    discountType,
    discountValue,
    longDistanceFeeType,
    longDistanceFeeValue,
    departureGarageId,
    returnGarageId,
  });

  // ─── Fetch initial data ────────────────────────────────────────

  useEffect(() => {
    fetch("/json/countries.json")
      .then((response) => response.json())
      .then((data) => setCountriesData(data))
      .catch((error) => console.error("Error loading countries data:", error));
  }, []);

  const { isLoading: isDetailLoading } = useBookingDetail({
    id,
    requestId,
    lang,
    setTransferType,
    setTransferFlags,
    setPickupLocation,
    setPickupLocationData,
    setDropoffLocation,
    setDropoffLocationData,
    setAirportDropoffLocations,
    setTravelDate,
    setFormatedTravelDate,
    setPickupTime,
    setDuration,
    setPassengers,
    setLuggage,
    setVehicles,
    setChildSeatAllocations,
    setBookingFor,
    setPassengerName,
    setPassengerTitle,
    setCustomer,
    setBookingKey,
    setBookingStatus,
    setPaymentStatus,
    setPrefixPhoneNumber,
    setPhoneNumber,
    setCountryCode,
    setMeetAssistS,
    setPickupFlightNo,
    setDropoffFlightNo,
    setPickupTerminal,
    setDropoffTerminal,
    setPickupSignHonorific,
    setPickupSign,
    setMessage,
    setTravelArea,
    setBasePriceType,
    setGarageType,
    setDiscountType,
    setDiscountValue,
    setLongDistanceFeeType,
    setLongDistanceFeeValue,
    setDepartureGarageId,
    setReturnGarageId,
    setGarageOptions,
  });

  // Pickup validation
  useEffect(() => {
    const isPickupFromAirportOrStation = transferFlags?.isFromAirport || transferFlags?.isFromStation;
    const isPickupMeetAssistRequired = transferFlags?.isFromAirport && checkLocationUseMeetService(pickupLocationData);

    if (isPickupFromAirportOrStation) {
      setIsPickupFlightInvalid(!pickupFlightNo || pickupFlightNo.trim() === "");
      if (transferFlags?.isFromAirport) {
        setIsPickupTerminalInvalid(!pickupTerminal || pickupTerminal.trim() === "");
      }
    } else {
      setIsPickupFlightInvalid(false);
      setIsPickupTerminalInvalid(false);
    }

    if (isPickupMeetAssistRequired) {
      setIsPickupSignInvalid(!pickupSign || pickupSign.trim() === "");
    } else {
      setIsPickupSignInvalid(false);
    }
  }, [pickupFlightNo, pickupTerminal, pickupSign, transferFlags, pickupLocationData]);

  // Dropoff validation
  useEffect(() => {
    const isDropoffToAirportOrStation = transferFlags?.isToAirport || transferFlags?.isToStation;

    if (isDropoffToAirportOrStation) {
      setIsDropoffFlightInvalid(!dropoffFlightNo || dropoffFlightNo.trim() === "");
      if (transferFlags?.isToAirport) {
        setIsDropoffTerminalInvalid(!dropoffTerminal || dropoffTerminal.trim() === "");
      }
    } else {
      setIsDropoffFlightInvalid(false);
      setIsDropoffTerminalInvalid(false);
    }
  }, [dropoffFlightNo, dropoffTerminal, transferFlags]);

  // ─── Location selection handlers ───────────────────────────────

  const pickupLocationSelected = (place) => {
    if (!place) return;
    enrichPlaceData(place);
    setPickupLocation(place.formatted_address || "");
    setPickupLocationData(place);
  };

  const dropoffLocationSelected = (place) => {
    if (!place) return;
    enrichPlaceData(place);
    setDropoffLocation(place.formatted_address || "");
    setDropoffLocationData(place);
  };

  const airportDropoffLocationSelected = (locationId, place) => {
    if (!place) return;
    enrichPlaceData(place);

    // Specified Destination only: reject a dropoff that repeats the location
    // right before it (pickup for the first dropoff, otherwise the previous
    // dropoff). Alert the admin and auto-remove the duplicate entry.
    const getPlaceId = (data) => data?.place_id || data?.value || null;
    const index = airportDropoffLocations.findIndex((dropoff) => dropoff.id === locationId);
    const selectedId = getPlaceId(place);
    const previousId =
      index <= 0
        ? getPlaceId(pickupLocationData)
        : getPlaceId(airportDropoffLocations[index - 1]?.data);
    const isDuplicateAdjacent = Boolean(selectedId && previousId && selectedId === previousId);

    if (isDuplicateAdjacent && airportDropoffLocations.length > 1) {
      setShowDuplicateLocationModal(true);
      setAirportDropoffLocations((prev) => prev.filter((dropoff) => dropoff.id !== locationId));
      return;
    }

    setAirportDropoffLocations((prev) =>
      prev.map((dropoff) =>
        dropoff.id === locationId
          ? { ...dropoff, address: place.formatted_address || "", data: place }
          : dropoff
      )
    );
  };

  const handleAddDestination = () => {
    setAirportDropoffLocations((prev) => {
      const nextId = prev.length
        ? Math.max(...prev.map((dropoff) => dropoff.id)) + 1
        : 1;
      return [...prev, { id: nextId, address: "", data: null }];
    });
  };

  const handleRemoveDestination = (locationId) => {
    setAirportDropoffLocations((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((dropoff) => dropoff.id !== locationId);
    });
  };

  // ─── Derived values ────────────────────────────────────────────

  const phoneCountryOptions = countriesData.map((country) => ({
    value: country.code,
    label: country.name,
    dialCode: country.dial_code,
    flag: country.flag,
  }));

  const formatPhoneCountry = (selectedCountryCode) => {
    if (!selectedCountryCode) return null;
    return phoneCountryOptions.find((option) => option.value === selectedCountryCode) || null;
  };

  const goToFallbackPage = () => {
    if (requestId && queryParams.get("from") === "modification-history" && id) {
      history.push(`/admin/bookinglist/edit/v2/${id}/modification-history/${requestId}`);
      return;
    }
    history.push(id ? `/admin/bookinglist/edit/v2/${id}` : "/admin/bookinglist");
  };

  const handleOpenDiscardModal = () => setShowDiscardModal(true);
  const handleCloseDiscardModal = () => setShowDiscardModal(false);
  const handleConfirmDiscardEdit = () => {
    setShowDiscardModal(false);
    goToFallbackPage();
  };

  // Block Specified Destination bookings where any adjacent locations match.
  const duplicateDropoffIds = getDuplicateAdjacentDropoffIds({
    transferType,
    pickupLocationData,
    airportDropoffLocations,
  });

  const handleReviewClick = () => {
    if (duplicateDropoffIds.size > 0) {
      basicSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (!vehicleValidation()) {
      vehicleSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (isPassengerNameInvalid || isPassengerPhoneInvalid) {
      passengerSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (isPickupFlightInvalid || isPickupTerminalInvalid || isPickupSignInvalid || isDropoffFlightInvalid || isDropoffTerminalInvalid) {
      additionalInfoSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    handleReviewChanges();
  };

  // ─── Render ────────────────────────────────────────────────────

  if (isDetailLoading) {
    return (
      <Layout>
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 w-100">
          <TitleBar title="Edit Booking" />
          <div className="container-fluid mb-5">
            <BookingEditSkeleton />
          </div>
        </main>
      </Layout>
    );
  }

  if (reviewSimulationData) {
    return (
      <BookingEditReview
        embedded
        simulationData={reviewSimulationData}
        idBookingProp={id}
        requestIdProp={requestId}
        onBackToEdit={() => { setReviewSimulationData(null); window.scrollTo(0, 0); }}
        onSubmitModificationRequest={handleSubmitModificationRequest}
        isSubmittingModification={isSubmittingModification}
      />
    );
  }

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 w-100">
        {/* <TitleBar title="Edit Booking" /> */}
        <div className="container-fluid mb-5 tw:text-[#212529] tw:font-hanken-grotesk">
          <ErrorModal
            handleClose={handleCloseErrorModal}
            show={false}
            text={errorModalText}
          />
          <ErrorModal
            handleClose={() => setShowDuplicateLocationModal(false)}
            show={showDuplicateLocationModal}
            text={t("editBooking.BookingEditPage.basicInformation.dropoffLocationSameValidation")}
          />
          <BookingHeader
            id={id}
            requestId={requestId}
            customer={customer}
            bookingKey={bookingKey}
            bookingStatus={bookingStatus}
            paymentStatus={paymentStatus}
            onDiscardClick={handleOpenDiscardModal}
          />

            <BasicInformationSection
              ref={basicSectionRef}
              duplicateDropoffIds={duplicateDropoffIds}
              transferType={transferType}
              setTransferType={setTransferType}
              pickupLocationSelected={pickupLocationSelected}
              pickupLocation={pickupLocation}
              pickupLocationData={pickupLocationData}
              dropoffLocationSelected={dropoffLocationSelected}
              dropoffLocation={dropoffLocation}
              dropoffLocationData={dropoffLocationData}
              airportDropoffLocations={airportDropoffLocations}
              airportDropoffLocationSelected={airportDropoffLocationSelected}
              handleAddDestination={handleAddDestination}
              handleRemoveDestination={handleRemoveDestination}
              LocationPinIcon={LocationPinIcon}
              duration={duration}
              setDuration={setDuration}
              durationOptions={durationOptions}
              travelDate={travelDate}
              setTravelDate={setTravelDate}
              setFormatedTravelDate={setFormatedTravelDate}
              pickupTime={pickupTime}
              setPickupTime={setPickupTime}
              travelArea={travelArea}
              setTravelArea={setTravelArea}
              formatTravelArea={formatTravelArea}
              travelAreaOptions={TRAVEL_AREA_OPTIONS}
              passengers={passengers}
              setPassengers={setPassengers}
              luggage={luggage}
              setLuggage={setLuggage}
              longDistanceFeeType={longDistanceFeeType}
              setLongDistanceFeeType={setLongDistanceFeeType}
              longDistanceFeeValue={longDistanceFeeValue}
              setLongDistanceFeeValue={setLongDistanceFeeValue}
            />

            <VehicleSelectionSection
              ref={vehicleSectionRef}
              vehicles={vehicles}
              isVehicleLoading={isVehicleLoading}
              handleRemoveVehicle={handleRemoveVehicle}
              handleChangeVehicle={handleChangeVehicle}
              formatVehicle={formatVehicle}
              vehicleOptions={vehicleOptions}
              renderVehicleCard={renderVehicleCard}
              handleAddVehicle={handleAddVehicle}
              isInvalid={isInvalidVehicle}
            />

            <PassengerInformationSection
              ref={passengerSectionRef}
              bookingFor={bookingFor}
              setBookingFor={setBookingFor}
              passengerTitle={passengerTitle}
              setPassengerTitle={setPassengerTitle}
              formatPassengerTitle={formatPassengerTitle}
              passengerTitleOptions={PASSENGER_TITLE_OPTIONS}
              passengerName={passengerName}
              setPassengerName={setPassengerName}
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              setPrefixPhoneNumber={setPrefixPhoneNumber}
              formatPhoneCountry={formatPhoneCountry}
              phoneCountryOptions={phoneCountryOptions}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              isNameInvalid={isPassengerNameInvalid}
              isPhoneInvalid={isPassengerPhoneInvalid}
            />

            <AdditionalInformationServicesSection
              ref={additionalInfoSectionRef}
              DEPARTURE_FLIGHT_ICON={DEPARTURE_FLIGHT_ICON}
              pickup={transferFlags?.isFromAirport || transferFlags?.isFromStation}
              dropoff={transferFlags?.isToAirport || transferFlags?.isToStation}
              pickupType={transferFlags?.isFromAirport ? "airport" : "station"}
              dropoffType={transferFlags?.isToAirport ? "airport" : "station"}
              pickupFlightNo={pickupFlightNo}
              setPickupFlightNo={setPickupFlightNo}
              dropoffFlightNo={dropoffFlightNo}
              setDropoffFlightNo={setDropoffFlightNo}
              pickupTerminal={pickupTerminal}
              setPickupTerminal={setPickupTerminal}
              dropoffTerminal={dropoffTerminal}
              setDropoffTerminal={setDropoffTerminal}
              terminalOptions={TERMINAL_OPTIONS}
              meetAssistS={meetAssistS}
              onChangeMeetAssist={onChangeMeetAssist}
              meetAssistPrice={meetAssistPrice}
              isPickupHaneda={checkIsPickupHaneda(pickupLocationData, transferFlags)}
              pickupLocation={pickupLocationData}
              dropoffLocation={lastDestinationData}
              isDropoffHaneda={checkIsDropoffHaneda(lastDestinationData, transferFlags)}
              isPickupUseMeetService={checkLocationUseMeetService(pickupLocationData)}
              isDropoffUseMeetService={checkLocationUseMeetService(lastDestinationData)}
              isPickupUseTerminalSelection={checkLocationUseTerminalSelection(pickupLocationData)}
              isDropoffUseTerminalSelection={checkLocationUseTerminalSelection(lastDestinationData)}
              pickupSignHonorific={pickupSignHonorific}
              setPickupSignHonorific={setPickupSignHonorific}
              formatPickupSignHonorific={formatPickupSignHonorific}
              passengerTitleOptions={PASSENGER_TITLE_WITH_EMPTY_OPTIONS}
              pickupSign={pickupSign}
              setPickupSign={setPickupSign}
              vehicles={vehicles}
              passengers={passengers}
              childSeatAllocations={childSeatAllocations}
              incrementChildSeat={incrementChildSeat}
              decrementChildSeat={decrementChildSeat}
              message={message}
              setMessage={setMessage}
              isPickupFlightInvalid={isPickupFlightInvalid}
              isPickupTerminalInvalid={isPickupTerminalInvalid}
              isPickupSignInvalid={isPickupSignInvalid}
              isDropoffFlightInvalid={isDropoffFlightInvalid}
              isDropoffTerminalInvalid={isDropoffTerminalInvalid}
            />

            <SettingsSection
              basePriceType={basePriceType}
              setBasePriceType={setBasePriceType}
              garageType={garageType}
              setGarageType={setGarageType}
              discountType={discountType}
              setDiscountType={setDiscountType}
              discountValue={discountValue}
              setDiscountValue={setDiscountValue}
              departureGarageId={departureGarageId}
              setDepartureGarageId={setDepartureGarageId}
              returnGarageId={returnGarageId}
              setReturnGarageId={setReturnGarageId}
              garageOptions={garageOptions}
              canUseCustomGarage={garageOptions.length > 1}
            />

          <div className="tw:my-[42px] tw:h-px tw:w-full tw:bg-[#F3F3F3]" />

          <ActionButtons
            getTypographyClass={getTypographyClass}
            onReviewChanges={handleReviewClick}
            isSimulatingReview={isSimulatingReview}
            onDiscardClick={handleOpenDiscardModal}
          />

          <DiscardEditConfirmationModal
            show={showDiscardModal}
            onClose={handleCloseDiscardModal}
            handleDiscardEdit={handleConfirmDiscardEdit}
            isSubmittingModification={false}
          />
        </div>
      </main>
    </Layout>
  );
};

export default BookingEditPage;
